import { useEffect, useState } from 'react'
import {
  ApiError,
  composeOrgBilling,
  getBillingPlans,
  getOrgBilling,
  openBillingPortal,
  startBillingCheckout,
  type AuthenticatedSession,
  type BillingComposition,
  type CataloguePlan,
  type OrgBillingSummary,
} from '../../api'
import {
  annualSavingsPercent,
  planAdvancedAddOns,
  planFeatures,
  planTagline,
  plansIncludeAlways,
} from '../../billingPlans'
import { disputeLockLabel } from '../../format'
import { count, dateOnly, formatUsd, planLabel } from '../../format'
import { AdminPage } from './AdminPage'

export function Billing({ session }: { session: AuthenticatedSession }) {
  return (
    <AdminPage
      session={session}
      title="Billing"
      intro="One price for the organization. Payment and invoices are handled in Stripe."
    >
      {(orgId) => <BillingBody orgId={orgId} />}
    </AdminPage>
  )
}

function BillingBody({ orgId }: { orgId: string }) {
  const [billing, setBilling] = useState<OrgBillingSummary | null>(null)
  const [plans, setPlans] = useState<CataloguePlan[]>([])
  const [trialDays, setTrialDays] = useState(14)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')

  useEffect(() => {
    let cancelled = false
    Promise.all([getOrgBilling(orgId), getBillingPlans()])
      .then(([b, cat]) => {
        if (cancelled) return
        setBilling(b)
        setPlans(cat.plans.filter((p) => !p.custom))
        setTrialDays(cat.trial_days)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load billing.')
      })
    return () => {
      cancelled = true
    }
  }, [orgId])

  async function managePayment() {
    setBusy('portal')
    setError('')
    try {
      const { url } = await openBillingPortal(orgId, window.location.href)
      window.location.assign(url)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open the billing portal.')
      setBusy('')
    }
  }

  async function choosePlan(priceId: string) {
    if (!priceId) return
    setBusy(priceId)
    setError('')
    try {
      const { url } = await startBillingCheckout(orgId, {
        price_id: priceId,
        success_url: window.location.href,
        cancel_url: window.location.href,
      })
      window.location.assign(url)
    } catch (e) {
      setError(checkoutRefusal(e))
      setBusy('')
    }
  }

  if (!billing) {
    return (
      <div className="card">
        <p className="muted">{error || 'Loading billing…'}</p>
      </div>
    )
  }

  const sub = billing.subscription
  const status = sub?.status ?? ''
  const effectivePlan = sub?.plan ?? billing.plan ?? 'team'
  const composition = billing.composition
  const limits = billing.limits
  const active = billing.active_envelopes ?? 0
  const completions = billing.completions_this_month ?? 0
  const trialing = status === 'trialing'

  return (
    <div className="stack">
      {error && <div className="notice notice-bad">{error}</div>}

      {!billing.write_allowed && (
        <div className="notice notice-bad">
          Sending is paused until billing is active. Add a payment method or choose a plan below.
        </div>
      )}

      <div className="card billing-summary">
        <div className="row-between billing-summary-head">
          <div>
            <div className="eyebrow">Current plan</div>
            <h2 className="billing-summary-title">
              {planLabel(effectivePlan)}
              {trialing && <span className="billing-plan-badge">Trial</span>}
              {status === 'active' && <span className="billing-plan-badge billing-plan-badge-good">Active</span>}
            </h2>
            {sub?.trial_end && trialing && (
              <p className="muted">Trial ends {dateOnly(sub.trial_end)}</p>
            )}
            {sub?.cancel_at_period_end && (
              <p className="muted">Cancels at period end</p>
            )}
          </div>
          <button className="btn" type="button" disabled={busy !== ''} onClick={() => void managePayment()}>
            {busy === 'portal' ? 'Opening…' : 'Payment & invoices'}
          </button>
        </div>

        {limits && (
          <div className="billing-summary-usage">
            <UsageRow label="Active envelopes" used={active} limit={limits.max_active_envelopes} />
            <UsageRow label="Completions this month" used={completions} limit={limits.max_completions_per_month} />
          </div>
        )}

        {composition && (
          <>
            <p className="small muted billing-summary-foot">
              Custom build: {composition.sender_seats} seats, {count(composition.completions_per_month)}{' '}
              completions/mo, {count(composition.active_envelopes)} active.
            </p>
            <AccountDisputeLock
              orgId={orgId}
              composition={composition}
              busy={busy}
              onBusy={setBusy}
              onUpdated={setBilling}
              onError={setError}
            />
          </>
        )}

        <p className="small muted billing-summary-foot">
          {trialFoot(trialDays, trialing, limits?.max_completions_per_month)} Invoices and cards live in Stripe&apos;s portal.
        </p>
      </div>

      <div>
        <div className="page-head billing-plans-head">
          <div className="eyebrow">Plans</div>
          <h2>Choose what fits your office</h2>
          <p>Plans differ by volume, team size, and how long records are kept.</p>
        </div>

        <div className="billing-plans">
          {plans.map((p) => (
            <PlanCard
              key={p.plan}
              plan={p}
              current={p.plan === effectivePlan}
              annualAvailable={billing.annual_available !== false}
              busy={busy}
              onChoose={choosePlan}
            />
          ))}
        </div>

        <div className="card card-tight billing-included">
          <div className="eyebrow">Every paid plan</div>
          <ul className="billing-included-list">
            {plansIncludeAlways.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>

        <div className="card card-tight billing-advanced">
          <div className="eyebrow">Advanced add-ons</div>
          <p className="small muted">
            While dispute lock is on, completed documents are kept past their retention date and the organization cannot be erased.
          </p>
          <ul className="billing-plan-features billing-advanced-list">
            {plans.map((p) => {
              const addOns = planAdvancedAddOns(p)
              const lock = addOns.find((a) => a.label === disputeLockLabel)
              if (!lock) return null
              return (
                <li key={p.plan} className={lock.included ? 'is-included' : 'is-excluded'}>
                  <span className="billing-plan-feature-mark" aria-hidden>
                    {lock.included ? '✓' : '—'}
                  </span>
                  <span>
                    <strong>{planLabel(p.plan)}</strong>: {lock.label}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </div>
  )
}

/**
 * What the trial allows, as the send gate counts it. A trial ends on its date,
 * not when a card is added, and it counts every document still out for
 * signature with the ones completed this month, so a new office with its
 * whole allowance out and nothing finished is already at it. The page once
 * said "completions/mo cap until you add a card", and both halves were false.
 */
export function trialFoot(trialDays: number, trialing: boolean, cap?: number): string {
  const start = `New organizations start with a ${trialDays}-day trial.`
  if (!trialing) return `${start} Full limits apply once billing is active.`
  const upTo = cap ? `, up to ${count(cap)}` : ''
  return `${start} Until the trial ends, documents out for signature and documents completed this month count together${upTo}.`
}

/**
 * Why checkout did not open. A custom build is billed monthly, and the server
 * refuses an annual price for one; the card leaves Annual out then, so this
 * is for a page loaded before the build changed.
 */
export function checkoutRefusal(e: unknown): string {
  if (e instanceof ApiError && e.code === 'annual_not_available') {
    return 'Annual billing is not available for a custom build. Choose the monthly price.'
  }
  return e instanceof Error ? e.message : 'Could not start checkout.'
}

/**
 * One plan's card. Annual is left out, the price and the button, when the
 * organization's build is billed monthly: checkout refuses an annual price
 * for it (review-2-server request 2), so offering one only led to a refusal.
 */
export function PlanCard({
  plan: p,
  current,
  annualAvailable = true,
  busy,
  onChoose,
}: {
  plan: CataloguePlan
  current: boolean
  annualAvailable?: boolean
  busy: string
  onChoose: (priceId: string) => void
}) {
  const monthly = p.prices.monthly_cents
  const annual = annualAvailable ? p.prices.annual_cents : undefined
  const savings = monthly && annual ? annualSavingsPercent(monthly, annual) : null
  const features = planFeatures(p)
  const tagline = planTagline(p.plan)

  return (
    <article className={`billing-plan-card${current ? ' is-current' : ''}${p.plan === 'team' ? ' is-featured' : ''}`}>
      <header className="billing-plan-card-head">
        <div className="row-between">
          <h3>{planLabel(p.plan)}</h3>
          {current && <span className="billing-plan-badge">Current</span>}
          {!current && p.plan === 'team' && <span className="billing-plan-badge billing-plan-badge-good">Popular</span>}
        </div>
        {tagline && <p className="billing-plan-tagline muted">{tagline}</p>}
      </header>

      {monthly != null && (
        <div className="billing-plan-price">
          <span className="billing-plan-amount">{formatUsd(monthly)}</span>
          <span className="billing-plan-period muted"> / month</span>
          {annual != null && (
            <p className="small muted billing-plan-annual">
              or {formatUsd(annual)} / year
              {savings != null && savings > 0 ? ` · save ${savings}%` : ''}
            </p>
          )}
        </div>
      )}

      <ul className="billing-plan-features">
        {features.map((f) => (
          <li key={f.label} className={f.included ? 'is-included' : 'is-excluded'}>
            <span className="billing-plan-feature-mark" aria-hidden>{f.included ? '✓' : '—'}</span>
            {f.label}
          </li>
        ))}
      </ul>

      <footer className="billing-plan-actions">
        {p.prices.monthly_price_id ? (
          <button
            className="btn btn-block"
            type="button"
            disabled={busy !== ''}
            onClick={() => onChoose(p.prices.monthly_price_id!)}
          >
            {busy === p.prices.monthly_price_id ? 'Opening…' : 'Monthly'}
          </button>
        ) : (
          <p className="small faint">Monthly billing is not available.</p>
        )}
        {annualAvailable && p.prices.annual_price_id && (
          <button
            className="btn btn-secondary btn-block"
            type="button"
            disabled={busy !== ''}
            onClick={() => onChoose(p.prices.annual_price_id!)}
          >
            {busy === p.prices.annual_price_id ? 'Opening…' : 'Annual'}
          </button>
        )}
      </footer>
    </article>
  )
}

/**
 * The dispute lock switch. It shows the build's legal_hold, which is what it
 * writes and what the erasure, the account deletion and the retention sweep
 * all read, trial or not. It read billing.dispute_lock once, which is the
 * entitlement and is false for every trial, so a trial organization that was
 * locked showed "Turn on", pressing it wrote the lock again, and it could not
 * be turned off until the trial ended.
 */
export function AccountDisputeLock({
  orgId,
  composition,
  busy,
  onBusy,
  onUpdated,
  onError,
}: {
  orgId: string
  composition: BillingComposition
  busy: string
  onBusy: (v: string) => void
  onUpdated: (b: OrgBillingSummary) => void
  onError: (msg: string) => void
}) {
  const enabled = composition.legal_hold
  const canEnable = composition.retention_days >= 365

  async function setDisputeLock(on: boolean) {
    if (on && !canEnable) {
      onError('Dispute lock requires at least one year of record retention on your build.')
      return
    }
    const next: BillingComposition = { ...composition, legal_hold: on }
    onBusy('dispute_lock')
    onError('')
    try {
      await composeOrgBilling(orgId, { composition: next, apply: true })
      onUpdated(await getOrgBilling(orgId))
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Could not update dispute lock.')
    } finally {
      onBusy('')
    }
  }

  return (
    <div className="billing-dispute-lock card card-tight">
      <div className="row-between">
        <div>
          <div className="eyebrow">Advanced add-on</div>
          <strong>{disputeLockLabel}</strong>
          <p className="small muted" style={{ marginBottom: 0 }}>
            While this is on, completed documents are kept past their retention date, and this
            organization cannot be erased.
          </p>
        </div>
        <button
          className="btn btn-secondary"
          type="button"
          disabled={busy !== '' || (!canEnable && !enabled)}
          onClick={() => void setDisputeLock(!enabled)}
        >
          {busy === 'dispute_lock' ? 'Saving…' : enabled ? 'Turn off' : 'Turn on'}
        </button>
      </div>
      {!canEnable && !enabled && (
        <p className="small faint" style={{ marginBottom: 0, marginTop: '0.75rem' }}>
          Raise retention to at least one year before enabling dispute lock.
        </p>
      )}
    </div>
  )
}

function UsageRow({ label, used, limit }: { label: string; used: number; limit: number }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0
  const high = pct >= 85
  return (
    <div className="billing-usage-row">
      <div className="billing-usage-head">
        <span>{label}</span>
        <span className="mono muted">
          {used} / {limit}
        </span>
      </div>
      <div className="billing-usage-bar" aria-hidden>
        <div
          className={`billing-usage-fill${high ? ' is-high' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}
